/**
 * Solana Wallet Connector
 * Supports ALL Solana wallets via Solana Wallet Adapter
 * Wallets: Phantom, Solflare, Backpack, Slope, Sollet, Glow, etc.
 */

import { PhantomWalletAdapter, SolflareWalletAdapter } from '@solana/wallet-adapter-wallets';
import { Connection, PublicKey } from '@solana/web3.js';
import { toast } from 'sonner';

// Solana connection
let solanaConnection: Connection | null = null;
let connectedWallet: any | null = null;

/**
 * Get Solana connection (cached)
 */
function getSolanaConnection(): Connection {
  if (!solanaConnection) {
    // Use public Solana RPC
    solanaConnection = new Connection(
      'https://api.mainnet-beta.solana.com',
      'confirmed'
    );
  }
  return solanaConnection;
}

/**
 * Connect Solana wallet
 * Auto-detects available wallets (Phantom, Solflare, etc.)
 */
export async function connectSolanaWallet(): Promise<{ address: string }> {
  try {
    // Try Phantom first (most popular)
    if ((window as any).solana?.isPhantom) {
      try {
        const phantom = (window as any).solana;
        const response = await phantom.connect();
        const address = response.publicKey.toString();
        
        connectedWallet = phantom;
        
        toast.success('Phantom Wallet Connected', {
          description: `Address: ${address.substring(0, 20)}...`,
          duration: 5000,
        });
        
        return { address };
      } catch (error: any) {
        if (error.code === 4001) {
          throw new Error('User rejected connection request');
        }
        throw error;
      }
    }
    
    // Try Solflare
    if ((window as any).solflare) {
      try {
        const solflare = (window as any).solflare;
        await solflare.connect();
        const address = solflare.publicKey.toString();
        
        connectedWallet = solflare;
        
        toast.success('Solflare Wallet Connected', {
          description: `Address: ${address.substring(0, 20)}...`,
          duration: 5000,
        });
        
        return { address };
      } catch (error: any) {
        if (error.code === 4001) {
          throw new Error('User rejected connection request');
        }
        throw error;
      }
    }
    
    // Try Backpack
    if ((window as any).backpack) {
      try {
        const backpack = (window as any).backpack;
        const response = await backpack.connect();
        const address = response.publicKey.toString();
        
        connectedWallet = backpack;
        
        toast.success('Backpack Wallet Connected', {
          description: `Address: ${address.substring(0, 20)}...`,
          duration: 5000,
        });
        
        return { address };
      } catch (error: any) {
        if (error.code === 4001) {
          throw new Error('User rejected connection request');
        }
        throw error;
      }
    }
    
    // No Solana wallet detected
    throw new Error('No Solana wallet detected. Please install Phantom, Solflare, or Backpack wallet.');
    
  } catch (error: any) {
    console.error('Solana wallet connection error:', error);
    toast.error('Solana Connection Failed', {
      description: error.message || 'Failed to connect Solana wallet',
      duration: 5000,
    });
    throw error;
  }
}

/**
 * Disconnect Solana wallet
 */
export async function disconnectSolanaWallet(): Promise<void> {
  try {
    if (connectedWallet && connectedWallet.disconnect) {
      await connectedWallet.disconnect();
    }
    connectedWallet = null;
    
    toast.info('Solana Wallet Disconnected', {
      description: 'Successfully disconnected from Solana wallet',
    });
  } catch (error) {
    console.error('Solana disconnect error:', error);
  }
}

/**
 * Get Solana balance
 */
export async function fetchSolanaBalance(address: string): Promise<number> {
  try {
    const connection = getSolanaConnection();
    const publicKey = new PublicKey(address);
    const balance = await connection.getBalance(publicKey);
    
    // Convert lamports to SOL (1 SOL = 1,000,000,000 lamports)
    return balance / 1e9;
  } catch (error) {
    console.error('Failed to fetch Solana balance:', error);
    return 0;
  }
}

/**
 * Check if Solana wallet is available
 */
export function isSolanaWalletAvailable(): boolean {
  return !!(
    (window as any).solana?.isPhantom ||
    (window as any).solflare ||
    (window as any).backpack
  );
}

